import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from '@/hooks/useForm.ts';
import { signUp as signUpService } from '@/services/authService';
import { useAuthStore, AuthState } from '@/stores/useAuthStore';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { AppButton, FormField, TextInput } from "@/components/ui/primitives";
import { submitSignUp } from "../utils/signUpValidation";

export const SignUpView = () => {
    const { t } = useTranslation(["auth", "common"]);
    const navigate = useNavigate();
    const token = useAuthStore((state: AuthState) => state.token);
    const { formState, onInputChange } = useForm({
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
    const [termsError, setTermsError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const termsInputRef = useRef<HTMLInputElement>(null);

    const {firstname, lastname, email, password, confirmPassword} = formState;

    useEffect(() => {
        if (token) {
            navigate({ to: '/dashboard', replace: true });
        }
    }, [token, navigate]);

    if (token) {
        return <div>{t("common.states.redirecting")}</div>;
    }

    const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage(null);
        setTermsError(null);

        try {
            setIsSubmitting(true);
            const validationError = await submitSignUp(formState, hasAcceptedTerms, signUpService);
            if (validationError === "requiredFields") {
                setErrorMessage(t("common.prompts.fillRequiredFields"));
                return;
            }
            if (validationError === "passwordMismatch") {
                setErrorMessage(t("common.prompts.passwordMismatch"));
                return;
            }
            if (validationError === "terms") {
                setTermsError(t("auth.signUp.termsRequired"));
                termsInputRef.current?.focus();
                return;
            }
            navigate({ to: '/auth/signin', replace: true });
        } catch {
            setErrorMessage(t("common.errors.createAccount"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="app-reveal w-full max-w-[440px] text-[var(--ds-ink)]">
            <div className="mb-6 text-left">
                <h1 className="font-[var(--ds-font-display)] text-[40px] font-medium leading-[1.1] tracking-[-0.03em]">{t("auth.signUp.title")}</h1>
                <p className="mt-3 leading-6 text-[var(--app-text-secondary)]">{t("auth.signUp.subtitle")}</p>
            </div>
            <form onSubmit={handleSignUp} noValidate className="mb-6 space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                    <FormField label={t("common.labels.firstName")} htmlFor="firstname" required className="[&_label]:!font-medium">
                        <TextInput
                            id="firstname"
                            type="text"
                            name="firstname"
                            autoComplete="given-name"
                            value={firstname}
                            placeholder="Juan"
                            onChange={onInputChange}
                            required
                        />
                    </FormField>
                    <FormField label={t("common.labels.lastName")} htmlFor="lastname" required className="[&_label]:!font-medium">
                        <TextInput
                            id="lastname"
                            type="text"
                            name="lastname"
                            autoComplete="family-name"
                            value={lastname}
                            placeholder="Vega"
                            onChange={onInputChange}
                            required
                        />
                    </FormField>
                </div>
                <FormField label={t("common.labels.email")} htmlFor="email" required className="[&_label]:!font-medium">
                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        placeholder="you@example.com"
                        onChange={onInputChange}
                        required
                    />
                </FormField>
                <FormField label={t("common.labels.password")} htmlFor="password" required className="[&_label]:!font-medium">
                    <div className="relative">
                        <TextInput
                            id="password"
                            type={showPassword ? "text" : "password"}
                            name="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={onInputChange}
                            inputClassName="pr-12"
                            required
                        />
                        <button
                            type="button"
                            className="absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-[var(--app-text-secondary)] transition hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-text)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--app-primary)_18%,transparent)]"
                            aria-label={showPassword ? t("common.actions.hidePassword") : t("common.actions.showPassword")}
                            aria-pressed={showPassword}
                            onClick={() => setShowPassword((value) => !value)}
                        >
                            {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                        </button>
                    </div>
                </FormField>
                <FormField label={t("auth.signUp.confirmPassword")} htmlFor="confirmPassword" required className="[&_label]:!font-medium">
                    <div className="relative">
                        <TextInput
                            id="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            name="confirmPassword"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={onInputChange}
                            inputClassName="pr-12"
                            required
                        />
                        <button
                            type="button"
                            className="absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-[var(--app-text-secondary)] transition hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-text)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--app-primary)_18%,transparent)]"
                            aria-label={showPassword ? t("common.actions.hidePassword") : t("common.actions.showPassword")}
                            aria-pressed={showPassword}
                            onClick={() => setShowPassword((value) => !value)}
                        >
                            {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                        </button>
                    </div>
                </FormField>

                <div>
                    <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm leading-6 text-[var(--app-text-secondary)]" htmlFor="terms-accepted">
                        <input
                            ref={termsInputRef}
                            id="terms-accepted"
                            name="termsAccepted"
                            type="checkbox"
                            checked={hasAcceptedTerms}
                            aria-required="true"
                            onChange={(event) => {
                                setHasAcceptedTerms(event.target.checked);
                                if (event.target.checked) setTermsError(null);
                            }}
                            aria-invalid={Boolean(termsError)}
                            aria-describedby={termsError ? "terms-accepted-error" : undefined}
                            className="mt-1 h-4 w-4 shrink-0 rounded border-[var(--app-border-strong)] accent-[var(--app-primary)]"
                        />
                        <span>
                            {t("auth.signUp.termsPrefix")} {" "}
                            <Link to="/terms" target="_blank" rel="noreferrer" className="font-semibold text-[var(--app-primary)] underline underline-offset-2 hover:text-[var(--app-primary-hover)]">
                                {t("auth.signUp.termsLink")}
                            </Link>
                            <span aria-hidden="true" className="ml-1 text-[var(--app-danger)]">*</span>
                        </span>
                    </label>
                    {termsError ? (
                        <p id="terms-accepted-error" className="mt-2 text-sm text-[var(--app-danger)]" role="alert">
                            {termsError}
                        </p>
                    ) : null}
                </div>

                {errorMessage ? (
                    <div className="rounded-xl border border-[color:color-mix(in_srgb,var(--app-danger)_24%,var(--app-border)_76%)] bg-[var(--app-danger-soft)] px-3 py-2 text-sm text-[var(--app-danger)]" role="alert">
                        {errorMessage}
                    </div>
                ) : null}

                <AppButton type="submit" tone="primary" size="md" fullWidth isLoading={isSubmitting} className="!font-medium !leading-none py-0 text-base md:min-h-11 md:py-0 md:text-base">
                    {isSubmitting ? t("common.states.saving", { defaultValue: "Saving" }) : t("auth.signUp.submit")}
                </AppButton>
            </form>
            <div className="mt-4 text-center">
                <div className="text-sm text-[var(--app-text-secondary)]">
                    {t("auth.signUp.hasAccount")}{" "}
                    <Link to={"/auth/signin"} className="inline-flex min-h-11 items-center text-[15px] font-medium leading-[22px] text-[var(--app-primary)] hover:text-[var(--app-primary-hover)] hover:underline">
                        {t("auth.signUp.signInLink")}
                    </Link>
                </div>
            </div>
        </div>
    );
};
